import type { ReactNode } from 'react'
import { count, money, percent } from './format'
import type { LinkKind, LinkRow, LinkTotals, SortKey } from './types'

type T = (key: string, opts?: Record<string, unknown>) => string

/** One optional column of the links table. Link and Profile always show. */
export interface Column {
  id: string
  sort?: SortKey
  /** Kinds it applies to; all when absent. */
  kinds?: LinkKind[]
  /** Shown until the viewer chooses otherwise. */
  byDefault: boolean
  /** Rendered right-aligned as a number. */
  num?: boolean
  cell: (row: LinkRow, t: T, ago: (iso: string | null | undefined) => string) => ReactNode
  /** The column's figure in the totals row (absent = blank). */
  total?: (totals: LinkTotals) => ReactNode
  tone?: (row: LinkRow) => number | null
}

const signed = (n: number | null) => (n == null ? '' : n >= 0 ? 'tl-good' : 'tl-bad')
const withShare = (n: number | null, share: number | null) =>
  n == null ? '—' : (
    <>
      {count(n)}
      {share != null && <span className="tl-share"> ({percent(share)})</span>}
    </>
  )
const notRead = (t: T) => <span className="dl-muted">{t('links.notRead')}</span>

export const COLUMNS: Column[] = [
  {
    id: 'clicks', sort: 'clicks', byDefault: true, num: true,
    cell: (r, t) => (
      <>
        {count(r.clicks)}
        {r.clicks_partial && <span className="tl-mark" title={t('kpi.clicksPartial')}>*</span>}
      </>
    ),
    total: (s) => count(s.clicks),
  },
  {
    id: 'claims', sort: 'claims', kinds: ['trial'], byDefault: true, num: true,
    cell: (r, t) => (
      <>
        {count(r.claims)}
        {r.claims_partial && <span className="tl-mark" title={t('kpi.claimsPartial')}>*</span>}
        <span className="tl-share"> / {r.claims_cap == null ? '∞' : count(r.claims_cap)}</span>
      </>
    ),
    total: (s) => count(s.claims),
  },
  {
    id: 'fans', sort: 'fans', byDefault: true, num: true,
    cell: (r, t) => (r.fans == null ? notRead(t) : <span className={r.fans_from_onlyfans ? 'dl-muted' : ''} title={r.fans_from_onlyfans ? t('kpi.fansFromOnlyFans') : undefined}>{count(r.fans)}</span>),
    total: (s) => count(s.fans),
  },
  { id: 'conversion', sort: 'conversion', byDefault: true, num: true, cell: (r) => percent(r.conversion), total: (s) => percent(s.conversion) },
  { id: 'replies', sort: 'replies', byDefault: false, num: true, cell: (r) => withShare(r.replies, r.replies_share), total: (s) => count(s.replies) },
  { id: 'spenders', sort: 'spenders', byDefault: true, num: true, cell: (r) => withShare(r.spenders, r.spenders_share), total: (s) => count(s.spenders) },
  {
    id: 'paid_after_trial', sort: 'paid_after_trial', kinds: ['trial'], byDefault: true, num: true,
    cell: (r) => count(r.paid_after_trial), total: (s) => count(s.paid_after_trial),
  },
  { id: 'cost', sort: 'cost', byDefault: true, num: true, cell: (r) => (r.cost ? money(r.cost) : <span className="dl-muted">—</span>), total: (s) => money(s.cost) },
  { id: 'earnings', sort: 'earnings', byDefault: true, num: true, cell: (r, t) => (r.earnings == null ? notRead(t) : money(r.earnings)), total: (s) => money(s.earnings) },
  {
    id: 'fleet_earnings', sort: 'fleet_earnings', byDefault: false, num: true,
    cell: (r, t) =>
      r.fleet_earnings == null ? '—' : (
        <>
          {money(r.fleet_earnings)}
          <span className="tl-sub tl-sub--num">{t('links.otherProfiles', { amount: money(r.fleet_other ?? 0) })}</span>
        </>
      ),
  },
  { id: 'profit', sort: 'profit', byDefault: true, num: true, cell: (r) => money(r.profit), tone: (r) => r.profit, total: (s) => money(s.profit) },
  { id: 'cpc', sort: 'cpc', byDefault: false, num: true, cell: (r) => money(r.cpc), total: (s) => money(s.cpc) },
  { id: 'epc', sort: 'epc', byDefault: false, num: true, cell: (r) => money(r.epc), total: (s) => money(s.epc) },
  { id: 'cpf', sort: 'cpf', byDefault: false, num: true, cell: (r) => money(r.cpf), total: (s) => money(s.cpf) },
  { id: 'arpf', sort: 'arpf', byDefault: false, num: true, cell: (r) => money(r.arpf), total: (s) => money(s.arpf) },
  { id: 'roi', sort: 'roi', byDefault: true, num: true, cell: (r) => percent(r.roi), tone: (r) => r.roi, total: (s) => percent(s.roi) },
  {
    id: 'updated', byDefault: true,
    cell: (r, t, ago) =>
      r.claimers_error ? (
        <span className="dl-status dl-status--bad" title={r.claimers_error}>
          <span className="dl-dot" /> {t('links.readFailed')}
        </span>
      ) : r.claimers_read_at ? (
        <span className="dl-muted">{ago(r.claimers_read_at)}</span>
      ) : (
        <span className="dl-muted">{t('links.onlyFansCounts')}</span>
      ),
  },
]

export const toneOf = (c: Column, r: LinkRow) => (c.tone ? signed(c.tone(r)) : '')
export const columnsFor = (kind: LinkKind) => COLUMNS.filter((c) => !c.kinds || c.kinds.includes(kind))
export const defaultColumns = (kind: LinkKind) => columnsFor(kind).filter((c) => c.byDefault).map((c) => c.id)
